import { useQuery, useQueryClient } from '@tanstack/react-query'
import i18n from 'i18next'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { ageDaysBucket, identify } from '@/lib/analytics'
import { api } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { useSession } from '@/lib/auth-client'
import { queryKeys } from '@/lib/query-keys'

// ============================================================================
// Types
// ============================================================================

type User = {
  id: string
  email: string
  name: string
  email_verified: boolean
  image: string | null
  created_at: string
  updated_at: string
  tour_completed: boolean
  locale: 'en' | 'ro'
}

type B2BUser = {
  id: string
  role: 'admin' | 'dispatcher' | 'truck_driver'
  phone: string | null
  status: 'active' | 'inactive' | 'invited' | 'suspended' | null
  carrier_company_id: string | null
}

type Customer = {
  id: string
  type: 'company' | 'individual'
  company_name: string | null
  phone: string | null
  display_name: string | null
}

type CarrierCompany = {
  id: string
  name: string
  cui: string
  verification_status: 'pending' | 'approved' | 'rejected'
  rating_avg: number | null
  rating_count: number | null
}

type UserRole = 'carrier' | 'customer' | 'none'

type AuthContextValue = {
  /** Current authenticated user */
  user: User | null
  /** User's role in the system */
  role: UserRole | null
  /** Carrier company info (if role is 'carrier') */
  company: CarrierCompany | null
  /** B2B user record (if role is 'carrier') */
  b2bUser: B2BUser | null
  /** Customer record (if role is 'customer') */
  customer: Customer | null
  /** Whether the user is a carrier */
  isCarrier: boolean
  /** Whether the user is a customer */
  isCustomer: boolean
  /** Whether the user has finished/skipped the new-user product tour */
  tourCompleted: boolean
  /** Whether auth data is loading */
  isLoading: boolean
  /** Auth error if any */
  error: Error | null
  /** Refetch auth data */
  refetch: () => Promise<void>
  /** Check if user is authenticated */
  isAuthenticated: boolean
  /** Better Auth session (for direct access) */
  session: ReturnType<typeof useSession>['data']
}

// ============================================================================
// Constants
// ============================================================================

const AUTH_ROLE_QUERY_KEY = queryKeys.auth.role

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue | null>(null)

// ============================================================================
// Provider
// ============================================================================

type AuthProviderProps = {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient()
  const { data: session, isPending: sessionLoading, error: sessionError } = useSession()

  // Track userId with ref to avoid effect firing on every session object change
  const prevUserIdRef = useRef<string | null>(null)
  const currentUserId = session?.user?.id ?? null
  const isAuthenticated = !!currentUserId

  // Fetch role data from /auth/me when we have a session
  const {
    data: roleData,
    isLoading: roleLoading,
    error: roleError,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: AUTH_ROLE_QUERY_KEY,
    queryFn: async () => {
      // Better Auth handles cookies automatically, so we just call the API
      const { data, error } = await api.auth.me.get()

      if (error) {
        throw new Error(getApiErrorMessage(error, 'Failed to fetch user role'))
      }

      return data
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error) => {
      // Don't retry on auth errors
      if (error.message.includes('Unauthorized')) {
        return false
      }
      return failureCount < 2
    },
  })

  const refetch = useCallback(async () => {
    await queryRefetch()
  }, [queryRefetch])

  // The active UI language comes from localStorage (i18next LanguageDetector).
  // On login, mirror that choice up to the server so it's persisted for
  // server-side copy — without clobbering the device's stored selection.
  const serverLocale = roleData?.user?.locale
  useEffect(() => {
    if (!isAuthenticated || !serverLocale) return
    const fromStorage = (i18n.language ?? '').slice(0, 2)
    const lang = fromStorage === 'en' || fromStorage === 'ro' ? fromStorage : null
    if (lang && lang !== serverLocale) {
      api.auth.me.locale.patch({ locale: lang }).then(() => {
        queryClient.invalidateQueries({ queryKey: AUTH_ROLE_QUERY_KEY })
      })
    }
  }, [isAuthenticated, serverLocale, queryClient])

  // Invalidate role data only when userId actually changes
  useEffect(() => {
    if (currentUserId !== prevUserIdRef.current) {
      prevUserIdRef.current = currentUserId
      if (currentUserId) {
        queryClient.invalidateQueries({ queryKey: AUTH_ROLE_QUERY_KEY })
      } else {
        queryClient.setQueryData(AUTH_ROLE_QUERY_KEY, null)
      }
    }
  }, [currentUserId, queryClient])

  const contextValue = useMemo<AuthContextValue>(() => {
    const user = roleData?.user ?? null
    const role = roleData?.role ?? null
    const company = roleData?.carrier_company ?? null
    const b2bUser = roleData?.b2b_user ?? null
    const customer = roleData?.customer ?? null

    return {
      user,
      role,
      company,
      b2bUser,
      customer,
      isCarrier: role === 'carrier',
      isCustomer: role === 'customer',
      tourCompleted: user?.tour_completed ?? false,
      isLoading: sessionLoading || roleLoading,
      error: (sessionError || roleError) as Error | null,
      refetch,
      isAuthenticated,
      session,
    }
  }, [
    roleData,
    sessionLoading,
    roleLoading,
    sessionError,
    roleError,
    refetch,
    isAuthenticated,
    session,
  ])

  // Attach non-PII session attributes to Umami so reports can segment by role,
  // company verification, customer type, etc. No emails/names/phones.
  const { user, role, company, customer, b2bUser, isAuthenticated: authed } = contextValue
  useEffect(() => {
    if (!authed || !role) return
    const accountAgeDays = user?.created_at
      ? Math.floor((Date.now() - new Date(user.created_at).getTime()) / 86_400_000)
      : null
    identify({
      user_id: user?.id,
      // NOTE: email + name are PII — only sent because explicitly requested.
      // Requires GDPR lawful basis + privacy-policy disclosure for EU users.
      email: user?.email,
      name: user?.name,
      role,
      carrier_company_id: company?.id,
      carrier_verification: company?.verification_status,
      carrier_role: b2bUser?.role,
      customer_id: customer?.id,
      customer_type: customer?.type,
      account_age_days_bucket: ageDaysBucket(accountAgeDays),
      has_rating: company?.rating_count ? company.rating_count > 0 : undefined,
    })
  }, [
    authed,
    user?.id,
    user?.email,
    user?.name,
    user?.created_at,
    role,
    company?.id,
    company?.verification_status,
    company?.rating_count,
    b2bUser?.role,
    customer?.id,
    customer?.type,
  ])

  return <AuthContext value={contextValue}>{children}</AuthContext>
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access auth context
 * Must be used within an AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }

  return context
}
